/**
 * WCAG 2.1 contrast ratio helpers used by the Profile Editor design panels.
 *
 * The formulas come straight from WCAG 2.1 §1.4.3 (Contrast Minimum):
 *   - Relative luminance: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 *   - Contrast ratio:     https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 *
 * We only handle hex color inputs because the editor's color pickers always
 * emit hex strings. Gradients / images / `currentColor` callers should
 * special-case their UI rather than try to feed those through here.
 */

export type Rgb = { r: number; g: number; b: number };

export function hexToRgb(hex: string | null | undefined): Rgb | null {
  if (!hex) return null;
  const cleaned = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned) && !/^[0-9a-fA-F]{3}$/.test(cleaned)) {
    return null;
  }
  const expanded =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned;
  const num = parseInt(expanded, 16);
  return {
    r: (num >> 16) & 0xff,
    g: (num >> 8) & 0xff,
    b: num & 0xff,
  };
}

function channelLuminance(value: number): number {
  const v = value / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(rgb: Rgb): number {
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

/**
 * Returns a WCAG contrast ratio (1.0 – 21.0) for a foreground/background pair,
 * or `null` when either color cannot be parsed.
 */
export function contrastRatio(
  foreground: string | null | undefined,
  background: string | null | undefined
): number | null {
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  if (!fg || !bg) return null;
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export type ContrastGrade = 'AAA' | 'AA' | 'AA Large' | 'Fail';

/**
 * WCAG grade for a given contrast ratio.
 *
 * - Normal text: AAA ≥ 7, AA ≥ 4.5, AA Large ≥ 3 (button-sized text), else Fail.
 * - Large text: AAA ≥ 4.5, AA ≥ 3, else Fail.
 *
 * "Large text" follows WCAG: ≥ 18pt regular or ≥ 14pt bold.
 */
export function wcagGrade(
  ratio: number,
  options: { largeText?: boolean } = {}
): ContrastGrade {
  if (options.largeText) {
    if (ratio >= 4.5) return 'AAA';
    if (ratio >= 3) return 'AA';
    return 'Fail';
  }
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA Large';
  return 'Fail';
}
